















































import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { validateEmail } from '@lib-shared/helpers';
import { translate } from '@lib-shared/helpers/translation.helper';
import { UniversalLink } from '../store/universal-link.constants';
import type { IUniversalLinkState } from '../store/universal-link.types';
import { EUniversalLinkAction } from '../store/universal-link.types';
import TopBar from '@signing-page/signing/components/TopBar.vue';
import LobbyCard from '@signing-page/lobby/components/LobbyCard.vue';
import type { ITheme } from '@signing-page/api/theme/types/theme';
import type { IVerifyLinkResponse } from '@signing-page/api/unversal-link/types/verify-link-response';
import Consent from '@signing-page/lobby/components/Consent.vue';
import { EAuthenticationStepType } from '@signing-page/authentication/types/enums/authentication-step-type';
import type { IAuthenticationStep } from '@signing-page/authentication/types/authentication-step';
import { ERedirectURLError } from '@signing-page/api/unversal-link/types/redirect-url-error';

@Component({
  components: {
    TopBar,
    LobbyCard,
    Consent,
  },
})
export default class UniversalLinkLobby extends Vue {
  @Prop() universalId: string;
  @Prop() theme: ITheme;

  @UniversalLink.State((state: IUniversalLinkState) => state.loading) loading: boolean;
  @UniversalLink.State((state: IUniversalLinkState) => state.loaded) loaded: boolean;
  @UniversalLink.State((state: IUniversalLinkState) => state.loadingError) loadingError: boolean;
  @UniversalLink.State((state: IUniversalLinkState) => state.verifyLinkResponse)
  verifyLinkResponse: IVerifyLinkResponse;
  @UniversalLink.State((state: IUniversalLinkState) => state.loadingRedirectURL)
  loadingRedirectURL: boolean;
  @UniversalLink.State((state: IUniversalLinkState) => state.redirectURL) redirectURL: string;
  @UniversalLink.State((state: IUniversalLinkState) => state.redirectURLError)
  redirectURLError: ERedirectURLError;

  @UniversalLink.Action(EUniversalLinkAction.VerifyLink) actionVerifyLink: (
    linkId: string
  ) => Promise<void>;
  @UniversalLink.Action(EUniversalLinkAction.LoadRedirectURL) actionLoadRedirectURL: ({
    gdprConsent,
    email,
    documentId,
  }: {
    gdprConsent: boolean;
    email: string;
    documentId: string;
  }) => Promise<void>;

  email = '';
  emailInvalid = false;
  consentGiven = false;

  get errorMessage(): string {
    if (!this.redirectURLError) {
      return '';
    }
    switch (this.redirectURLError) {
      case ERedirectURLError.GDPRConsentRequired:
        return translate('gdpr-consent-required');
      case ERedirectURLError.FailedToSaveGDPRConsent:
        return translate('failed-to-save-gdpr-consent');
      default:
        return translate('could-not-verify-your-email');
    }
  }

  get helpMessage(): string {
    if (this.emailInvalid) {
      return 'invalid-email';
    }
    return '';
  }

  get requireGdprConsent(): boolean {
    return this.verifyLinkResponse?.requireGdprConsent;
  }

  get consentStep(): IAuthenticationStep {
    return {
      type: EAuthenticationStepType.GdprConsent,
      data: {
        text: this.verifyLinkResponse.consentText,
        textData: {
          entity: {
            name: this.verifyLinkResponse.entityName,
          },
        },
      },
    };
  }

  get documentName(): string {
    return this.verifyLinkResponse?.documentName;
  }

  get documentId(): string {
    return this.verifyLinkResponse?.documentId;
  }

  get submitDisabled(): boolean {
    if (this.requireGdprConsent && !this.consentGiven) {
      return true;
    }
    return !this.email;
  }

  @Watch('redirectURL')
  redirect(newValue: string, oldValue: string) {
    if (newValue && !oldValue) {
      window.location.href = newValue;
    }
  }

  @Watch('documentId')
  emitDocumentLoaded(documentId: string) {
    if (documentId) {
      this.$emit('documentLoaded', documentId);
    }
  }

  setConsent(given: boolean) {
    this.consentGiven = given;
  }

  submit(gdprConsent: boolean, email: string, documentId: string) {
    if (!validateEmail(this.email?.toLowerCase())) {
      this.emailInvalid = true;
      return;
    }
    this.actionLoadRedirectURL({ gdprConsent, email: email?.toLowerCase(), documentId });
  }

  mounted() {
    if (this.redirectURL) {
      window.location.href = this.redirectURL;
      return;
    }
    this.actionVerifyLink(this.universalId);
  }
}
